import { useMemo, useState } from "react";

import {
  Button,
  type Column,
  EmptyState,
  Icon,
  Pill,
  SearchInput,
  Select,
  Table,
  Tabs,
  type TabItem,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useAutospedEmpresas } from "@/hooks/useAutosped";
import type { EmpresaRow, StatusAgg } from "@/types/autosped";
import { getInitials } from "@/utils/initials";

import { DispatchModal } from "./DispatchModal";
import { formatDateTime, origemLabel, statusAggPill } from "./helpers";
import "@/styles/operacao.css";

type StatusTab = "todos" | StatusAgg;
const PAGE_SIZE = 20;

const STATUS_TABS: { id: StatusTab; label: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "concluido", label: "Concluídos" },
  { id: "parcial", label: "Com falhas" },
  { id: "processando", label: "Em processamento" },
  { id: "erro", label: "Todos com erro" },
  { id: "pendente", label: "Pendentes" },
];

export function AutospedList({
  workspaceId,
  onOpen,
}: {
  workspaceId: number;
  onOpen: (e: EmpresaRow) => void;
}) {
  // v1: carrega a página de empresas e filtra/pagina por status no cliente (poucas
  // empresas por workspace). O detalhe — que é o pesado — usa paginação no servidor.
  const { data, isLoading } = useAutospedEmpresas(workspaceId, { limit: 200 });
  const rows = useMemo(() => data?.data ?? [], [data]);

  const [status, setStatus] = useState<StatusTab>("todos");
  const [origem, setOrigem] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [dispatching, setDispatching] = useState(false);

  const counts = useMemo(() => {
    const c: Record<string, number> = { todos: rows.length };
    for (const r of rows) c[r.status_agg] = (c[r.status_agg] ?? 0) + 1;
    return c;
  }, [rows]);

  const origemOptions = useMemo(
    () => [...new Set(rows.map((r) => r.origem).filter((o): o is string => o != null))],
    [rows],
  );

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "todos" && r.status_agg !== status) return false;
      if (origem && r.origem !== origem) return false;
      if (qq && !`${r.legal_name} ${r.document_masked}`.toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [rows, status, origem, q]);

  const totalArquivos = rows.reduce((s, r) => s + r.total, 0);
  const arquivosOk = rows.reduce((s, r) => s + r.ok, 0);
  const precisamAtencao = (counts.erro ?? 0) + (counts.pendente ?? 0);

  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const visibleKeys = pageRows.map((e) => String(e.client_id));
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach((k) => next.delete(k));
      else visibleKeys.forEach((k) => next.add(k));
      return next;
    });

  const tabs: TabItem<StatusTab>[] = STATUS_TABS.map((t) => ({
    id: t.id,
    label: t.label,
    count: counts[t.id] ?? 0,
  }));

  const columns: Column<EmpresaRow>[] = [
    {
      id: "empresa",
      header: "Empresa",
      cell: (e) => (
        <div className="emp-table-name">
          <span className="org-mono-mark">{getInitials(e.legal_name)}</span>
          <div>
            <div className="razao">{e.legal_name}</div>
            <div className="fantasia">{e.document_masked}</div>
          </div>
        </div>
      ),
    },
    {
      id: "origem",
      header: "Origem",
      cell: (e) => (
        <span className="op-origem">
          <Icon name={e.origem === "ecac" ? "zap" : "user"} size={12} />
          {origemLabel(e.origem)}
        </span>
      ),
    },
    {
      id: "ultimo",
      header: "Último recebido",
      cell: (e) => <span className="op-mono">{formatDateTime(e.ultimo_recebido)}</span>,
    },
    {
      id: "qtd",
      header: "Quantidade",
      align: "right",
      cell: (e) =>
        e.total > 0 ? (
          <span className="op-mono">
            <strong className={e.ok === e.total ? "" : e.erro > 0 ? "op-danger" : "op-warning"}>
              {e.ok}
            </strong>
            <span className="op-muted">/{e.total}</span>
          </span>
        ) : (
          <span className="op-muted">—</span>
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: (e) => {
        const sp = statusAggPill(e.status_agg);
        return <Pill tone={sp.tone}>{sp.label}</Pill>;
      },
    },
  ];

  return (
    <div>
      <div className="op-head">
        <div className="op-summary">
          <strong>{rows.length} empresas no ciclo</strong> · {arquivosOk} de {totalArquivos}{" "}
          arquivos rodados · {precisamAtencao} precisam de atenção
        </div>
        <div className="op-head-actions">
          <Guard permission="autosped.dispatch">
            <Button variant="primary" size="sm" iconLeft="plus" onClick={() => setDispatching(true)}>
              Nova solicitação
            </Button>
          </Guard>
        </div>
      </div>

      {dispatching && (
        <DispatchModal workspaceId={workspaceId} onClose={() => setDispatching(false)} />
      )}

      <Tabs
        items={tabs}
        active={status}
        onChange={(s) => {
          setStatus(s);
          setPage(0);
        }}
      />

      <div className="op-filters">
        <SearchInput
          value={q}
          onChange={(v) => {
            setQ(v);
            setPage(0);
          }}
          placeholder="Buscar empresa ou CNPJ…"
        />
        {origemOptions.length > 0 && (
          <Select
            variant="chip"
            label="Origem"
            value={origem}
            onChange={(v) => {
              setOrigem(v);
              setPage(0);
            }}
            options={origemOptions.map((o) => ({ value: o, label: origemLabel(o) }))}
            clearable
          />
        )}
        {(status !== "todos" || origem || q) && (
          <Button
            variant="ghost"
            size="sm"
            iconLeft="x"
            onClick={() => {
              setStatus("todos");
              setOrigem(null);
              setQ("");
              setPage(0);
            }}
          >
            Limpar
          </Button>
        )}
        <span className="op-counter">
          {filtered.length} {filtered.length === 1 ? "execução" : "execuções"}
        </span>
      </div>

      {selected.size > 0 && (
        <div className="op-bulkbar">
          <span className="op-bulkbar-count">
            {selected.size} {selected.size === 1 ? "empresa selecionada" : "empresas selecionadas"}
          </span>
          <div className="op-bulkbar-actions">
            <button type="button" className="op-bulkbar-btn" onClick={() => setSelected(new Set())}>
              <Icon name="x" size={13} /> Limpar
            </button>
          </div>
        </div>
      )}

      <Table
        rows={pageRows}
        columns={columns}
        getKey={(e) => String(e.client_id)}
        onRowClick={onOpen}
        selection={{ selected, toggle, toggleAll, allSelected }}
        empty={
          <EmptyState
            icon="search"
            title={isLoading ? "Carregando…" : "Nenhuma execução encontrada"}
          />
        }
      />

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="op-pagination">
      <button
        type="button"
        className="op-page-btn"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        <Icon name="arrow-left" size={14} /> Anterior
      </button>
      <span className="op-page-info">
        {page + 1} de {totalPages}
      </span>
      <button
        type="button"
        className="op-page-btn"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
      >
        Próxima <Icon name="arrow-up-right" size={14} />
      </button>
    </div>
  );
}
